/**
 * Utility functions for dispute workflow calculations.
 * Connects disputes with their dispute type workflow configurations.
 *
 * NOTE: These utilities are now fallback-only for pre-migration disputes that don't have
 * snapshotted workflow stages. New disputes snapshot their stages at creation time via
 * the DisputeWorkflowStages table. Callers should prefer snapshot data from
 * useDisputeWorkflowStages() and only fall back to these functions when no snapshot
 * is available.
 */

import { generateMockDisputeTypes } from '@/lib/mock-data/dispute-types-mock-data';
import type { DisputeType } from '@/types/dispute-types';

// Cache for dispute types to avoid repeated lookups
let disputeTypesCache: DisputeType[] | null = null;

/**
 * Get all dispute types (with caching)
 */
function getDisputeTypes(): DisputeType[] {
  if (!disputeTypesCache) {
    disputeTypesCache = generateMockDisputeTypes();
  }
  return disputeTypesCache;
}

/**
 * Get the total number of workflow levels for a dispute type
 * @param disputeTypeName - The name of the dispute type (e.g., "Billing Error")
 * @returns The total number of workflow stages/levels
 */
export function getTotalLevelsForDisputeType(disputeTypeName: string): number {
  const disputeTypes = getDisputeTypes();
  const disputeType = disputeTypes.find((dt) => dt.name === disputeTypeName);

  if (!disputeType?.workflow?.stages) {
    // Default to 4 levels if dispute type not found
    return 4;
  }

  return disputeType.workflow.stages.length;
}

/**
 * Get workflow stages for a dispute type
 * @param disputeTypeName - The name of the dispute type
 * @returns Array of workflow stages
 */
export function getWorkflowStagesForDisputeType(disputeTypeName: string) {
  const disputeTypes = getDisputeTypes();
  const disputeType = disputeTypes.find((dt) => dt.name === disputeTypeName);

  if (!disputeType?.workflow?.stages) {
    return [];
  }

  return disputeType.workflow.stages;
}

/**
 * Get the dispute type configuration by name
 * @param disputeTypeName - The name of the dispute type
 * @returns The dispute type configuration or null
 */
export function getDisputeTypeByName(disputeTypeName: string): DisputeType | null {
  const disputeTypes = getDisputeTypes();
  return disputeTypes.find((dt) => dt.name === disputeTypeName) || null;
}

/**
 * Calculate workflow progress percentage
 * @param currentLevel - Current workflow level (1-based)
 * @param disputeTypeName - The name of the dispute type
 * @returns Progress percentage (0-100)
 */
export function calculateWorkflowProgress(currentLevel: number, disputeTypeName: string): number {
  const totalLevels = getTotalLevelsForDisputeType(disputeTypeName);
  return Math.round((currentLevel / totalLevels) * 100);
}

/**
 * Clear the dispute types cache (useful for testing or when data is updated)
 */
export function clearDisputeTypesCache(): void {
  disputeTypesCache = null;
}

/**
 * Get the derived status for a dispute based on the actual API status,
 * workflow level, and closed status.
 * @param currentLevel - Current workflow level (1-based)
 * @param totalLevels - Total number of workflow levels
 * @param closedReason - Optional closed reason if dispute was closed early
 * @param actualStatus - The status string returned by the API (e.g. "Escalated")
 * @returns Status string: "New", "In Progress", "Escalated", "Resolved", or "Closed"
 */
export function getDerivedStatus(
  currentLevel: number,
  totalLevels: number,
  closedReason?: string | null,
  actualStatus?: string | null,
): string {
  // Respect the actual API status as the primary source of truth for active statuses.
  // This ensures reopened disputes display the correct status even when stale
  // closed-reason data hasn't been fully cleared yet.
  if (actualStatus && actualStatus !== 'Closed' && actualStatus !== 'Resolved') {
    return actualStatus;
  }

  // If dispute was closed early (non-empty closedReason), return "Closed"
  if (closedReason?.trim()) {
    return 'Closed';
  }

  // Respect the actual status from the API when it is Resolved or Closed
  if (actualStatus === 'Resolved' || actualStatus === 'Closed') {
    return actualStatus;
  }

  // Otherwise derive from workflow level
  if (currentLevel === 1) return 'New';
  if (currentLevel === totalLevels) return 'Resolved';
  return 'In Progress';
}

/**
 * Get a human-readable label for a closed reason
 * @param closedReason - The closed reason code
 * @returns Human-readable label
 */
export function getClosedReasonLabel(closedReason: string): string {
  const labels: Record<string, string> = {
    duplicate: 'Duplicate',
    customer_withdrew: 'Customer Withdrew',
    invalid_dispute: 'Invalid Dispute',
    other: 'Other',
  };
  return labels[closedReason] || closedReason;
}

/**
 * Get a human-readable label for a resolution outcome
 * @param outcome - The resolution outcome code
 * @returns Human-readable label
 */
export function getResolutionOutcomeLabel(outcome: string): string {
  const labels: Record<string, string> = {
    upheld: 'Upheld (Customer Won)',
    denied: 'Denied (Merchant Won)',
    partial: 'Partial Resolution',
  };
  return labels[outcome] || outcome;
}

/**
 * Get a description for a resolution outcome
 * @param outcome - The resolution outcome code
 * @returns Description text
 */
export function getResolutionOutcomeDescription(outcome: string): string {
  const descriptions: Record<string, string> = {
    upheld: "Customer's dispute is valid. Transaction will be reversed.",
    denied: 'Dispute is not valid. Transaction stands as is.',
    partial: 'Partial credit issued to customer.',
  };
  return descriptions[outcome] || '';
}

/**
 * Check if a dispute is at the final workflow level
 * @param currentLevel - Current workflow level
 * @param totalLevels - Total number of workflow levels
 * @returns True if at final level
 */
export function isAtFinalLevel(currentLevel: number, totalLevels: number): boolean {
  return currentLevel === totalLevels;
}

/**
 * Check if a dispute can be resolved (is at second-to-last level)
 * @param currentLevel - Current workflow level
 * @param totalLevels - Total number of workflow levels
 * @returns True if can progress to resolution
 */
export function canProgressToResolution(currentLevel: number, totalLevels: number): boolean {
  return currentLevel === totalLevels - 1;
}
